import { Component, inject, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { AUTH_SERVICE, AuthService } from '../../services/auth.service';
import { Router, RouteReuseStrategy } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { LogService } from '../../services/log.service';
import { Log } from '../../models/log';
import { MatRippleModule } from '@angular/material/core';
import { ApprovePsychologistComponent } from '../../components/approve-psychologist/approve-psychologist.component';
import TYPES from '../../constants/types';
import { CustomRouteReuseStrategy } from '../../util/custom-route-reuse-strategy';
import { ApproveUserConnnectionComponent } from '../../components/approve-user-connnection/approve-user-connnection.component';
import { MessageAlertComponent } from "../../components/message-alert/message-alert.component";
import { UserPsychologistService } from '../../services/user-psychologist.service';
import { UserPsychologist } from '../../models/user-psychologist';
import { PsychologistService } from '../../services/psychologist.service';
import { Psychologist } from '../../models/psychologists';
import { ConfirmAppointmentComponent } from "../../components/confirm-appointment/confirm-appointment.component";
import { ConfigService } from '../../services/config.service';
import { RequestNotificationsComponent } from "../../components/request-notifications/request-notifications.component";
import { PushService } from '../../services/push.service';
import { InviteService } from '../../services/invite.service';
import { Invite } from '../../models/invite';

@Component({
  selector: 'app-greetings',
  imports: [
    TranslatePipe,
    MatRippleModule,
    ApprovePsychologistComponent,
    ApproveUserConnnectionComponent,
    MessageAlertComponent,
    ConfirmAppointmentComponent,
    RequestNotificationsComponent
],
  templateUrl: './greetings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './greetings.component.scss'
})
export class GreetingsComponent {

  authService: AuthService = inject(AUTH_SERVICE);
  logService: LogService<Log> = inject(LogService);
  psychologistService: PsychologistService<Psychologist> = inject(PsychologistService);
  userPsychologistService: UserPsychologistService<UserPsychologist> = inject(UserPsychologistService);
  inviteService: InviteService<Invite> = inject(InviteService<Invite>);
  configService : ConfigService = inject(ConfigService);
  pushService : PushService = inject(PushService);
  userPsychologist: WritableSignal<UserPsychologist | null> = signal<UserPsychologist| null>(null);
  loading: WritableSignal<boolean | null> = signal<boolean| null>(true);
  loadingAcceptingInvite: WritableSignal<boolean | null> = signal<boolean| null>(true);
  
  originalState = {
    psychologist_connection : true,
    notifications: true
  }

  PROFILES: any;
  NOTIFICATION_STATUS: any;

  constructor(
    private router: Router,
    private reuseStrategy: RouteReuseStrategy
  ){
    this.logService.canAccessMindLogRegister.set(false);
    this.PROFILES = TYPES.DATABASE.PROFILES;   
    this.NOTIFICATION_STATUS = TYPES.UI.NOTIFICATION_STATUS;

    const inviteToken = localStorage.getItem('inviteToken');
    const userEmail = this.authService.user()?.email;
    if(inviteToken && userEmail){
      this.loadingAcceptingInvite.set(true);
      this.inviteService.getByTokenAndEmail(inviteToken, userEmail).then(
        (data) => {
          const inviteId = data.id;
          const userId = this.authService.user()?.id;
          const psychologistId = data.psychologist_id;
          const psychologistUserId = data.psychologists?.user_id;
          if(inviteId && userId && psychologistId && psychologistUserId){
            this.inviteService.inviteAccepted(inviteId, userId, psychologistId, psychologistUserId).then(
              () => {
                localStorage.removeItem('inviteToken');
                this.loadingAcceptingInvite.set(false);

                this.loading.set(true);
                this.userPsychologistService.getUserPsychologistByUserId(userId).then(
                  (data) => {
                    this.userPsychologist.set(data);
                    this.loading.set(false);
                  }
                );
                this.originalState = JSON.parse(localStorage.getItem('originalState') || '{"psychologist_connection": true, "notifications": true}');
              }
            )
          }
        }
      )
    }

    if(this.configService.getNotificationStatus() === TYPES.UI.NOTIFICATION_STATUS.GRANTED){
      const userId = this.authService.user()?.id;
      if(userId){  
        this.pushService.requestPermissionAndSubscribe(userId);
      }       
    }

    if(this.authService.user()?.profile === this.PROFILES.PSYCHOLOGIST.key){
      //Delete the previous cache from historical screen
      const customReuseStrategy = this.reuseStrategy as CustomRouteReuseStrategy;
      customReuseStrategy.clearRoute('home/(home:historical)');
      customReuseStrategy.clearRoute('(primary:complete-account)');

      const userId = this.authService.user()?.id;
      if(userId){
        this.psychologistService.getPsychologistByUserId(userId).then(
          (data) => {}
        );
      }
    }

    const userId = this.authService.user()?.id;
    if(userId){
      this.loading.set(true);
      this.userPsychologistService.getUserPsychologistByUserId(userId).then(
        (data) => {
          this.userPsychologist.set(data);
          this.loading.set(false);
        }
      );
      this.originalState = JSON.parse(localStorage.getItem('originalState') || '{"psychologist_connection": true, "notifications": true}');
    }
  }

  getGreeting(): string {
    const currentHour = new Date().getHours();

    if (currentHour >= 6 && currentHour < 12) {
      return "greetings.morning";
    } else if (currentHour >= 12 && currentHour < 18) {
      return "greetings.afternoon";
    } else {
      return "greetings.evening";
    }
  }

  goToFeeling(){
    this.logService.resetLogState();
    this.logService.canAccessMindLogRegister.set(true);

    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }

    this.router.navigate(['/mood']);
  }
}
